// Perguntas da primeira vez (AC-127): um único pop-up, uma pergunta por vez — idioma, tema e
// formato do horário — trocando com efeito deslizante. Não dá para pular nem fechar; cada
// resposta vale na hora (ASM-063) e fica no perfil (uma vez por conta).
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, defaultTimeFormat, detectLanguage, isLanguage, type Language, type ProfilePatch, type ThemePref, type TimeFormat } from '../domain/settings';
import { useAppState, useStore } from '../state/AppStore';
import { BeeLogo, Icon, type IconName } from './Icon';
import { Flag } from './Flag';
import { cls } from './NewTaskForm';

type Step = 'language' | 'theme' | 'time';
const STEPS: Step[] = ['language', 'theme', 'time'];

function systemLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return ['en'];
  return navigator.languages?.length ? navigator.languages : [navigator.language];
}

function Choice({ on, label, hint, icon, lead, active, onPick }: { on: boolean; label: string; hint?: string; icon?: IconName; lead?: ReactNode; active: boolean; onPick(): void }) {
  return (
    <button type="button" role="radio" aria-checked={on} tabIndex={active ? 0 : -1} className={cls('onb__choice', on && 'is-on')} onClick={onPick}>
      {lead}
      {icon && <Icon name={icon} size={16} />}
      <span className="onb__choice-label">{label}</span>
      {hint && <span className="onb__choice-hint">{hint}</span>}
      {on && <Icon name="check" size={15} className="onb__choice-check" />}
    </button>
  );
}

export function Onboarding() {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const profile = useAppState((s) => s.profile);
  const [step, setStep] = useState(0);
  if (!profile) return null;

  // sem escolha ainda: o idioma em que o app já está (último usado neste aparelho ou o do sistema)
  const language: Language = profile.language ?? (isLanguage(i18n.language) ? i18n.language : detectLanguage(systemLanguages()));
  const save = (patch: ProfilePatch) => void store.updateProfile(patch);
  // escolher o idioma já marca o formato comum dele (ASM-062); dá para trocar na pergunta 3
  const pickLanguage = (lang: Language) => save({ language: lang, time_format: defaultTimeFormat(lang) });
  const last = step === STEPS.length - 1;

  const next = () => {
    if (STEPS[step] === 'language' && profile.language !== language) pickLanguage(language);
    if (last) save({ onboarding_done: true });
    else setStep(step + 1);
  };

  return (
    <div className="overlay overlay--onb">
      <div className="dialog onb" role="dialog" aria-modal="true" aria-labelledby="onb-title" data-testid="onboarding">
        <div className="onb__head">
          <BeeLogo size={32} />
          <h2 id="onb-title" className="dialog__title">
            {t('onboarding.title')}
          </h2>
          <span className="onb__step">{t('onboarding.step', { n: step + 1, total: STEPS.length })}</span>
        </div>

        <div className="onb__viewport">
          <div className="onb__track" style={{ transform: `translateX(-${step * 100}%)` }} data-step={STEPS[step]}>
            <section className="onb__slide" aria-hidden={step !== 0}>
              <h3 className="onb__q">{t('onboarding.languageQ')}</h3>
              <div className="onb__choices onb__choices--grid" role="radiogroup" aria-label={t('onboarding.languageQ')}>
                {LANGUAGES.map((lang) => (
                  <Choice key={lang} on={language === lang} label={t(`languages.${lang}`)} lead={<Flag lang={lang} />} active={step === 0} onPick={() => pickLanguage(lang)} />
                ))}
              </div>
            </section>

            <section className="onb__slide" aria-hidden={step !== 1}>
              <h3 className="onb__q">{t('onboarding.themeQ')}</h3>
              <div className="onb__choices" role="radiogroup" aria-label={t('onboarding.themeQ')}>
                {(
                  [
                    ['light', 'settings.themeLight', 'sun'],
                    ['dark', 'settings.themeDark', 'moon'],
                    ['system', 'settings.themeSystem', 'monitor'],
                  ] as Array<[ThemePref, string, IconName]>
                ).map(([value, label, icon]) => (
                  <Choice key={value} on={profile.theme === value} label={t(label)} icon={icon} active={step === 1} onPick={() => save({ theme: value })} />
                ))}
              </div>
            </section>

            <section className="onb__slide" aria-hidden={step !== 2}>
              <h3 className="onb__q">{t('onboarding.timeQ')}</h3>
              <div className="onb__choices" role="radiogroup" aria-label={t('onboarding.timeQ')}>
                {(['24h', '12h'] as TimeFormat[]).map((value) => (
                  <Choice
                    key={value}
                    on={profile.time_format === value}
                    label={value === '24h' ? t('settings.time24') : t('settings.time12')}
                    icon="clock"
                    active={step === 2}
                    onPick={() => save({ time_format: value })}
                  />
                ))}
              </div>
            </section>
          </div>
        </div>

        <div className="onb__foot">
          <div className="onb__dots" aria-hidden="true">
            {STEPS.map((s, i) => (
              <span key={s} className={cls('onb__dot', i === step && 'is-on')} />
            ))}
          </div>
          {step > 0 && (
            <button type="button" className="btn btn--ghost" onClick={() => setStep(step - 1)}>
              {t('onboarding.back')}
            </button>
          )}
          <button type="button" className="btn btn--primary" onClick={next}>
            {last ? t('onboarding.finish') : t('onboarding.next')}
            {!last && <Icon name="chevronRight" size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
}
